import React from 'react';
import { Control } from 'react-hook-form';
import { FormInput, FormSelect } from '@/components/Common/FormFields';
import { CreateArrayOptions, LEVEL_OPTIONS, REQ_OPTIONS } from '@/constants';

type Option = { value: string | number; label: string };

type LessonDetailsSectionProps = {
  control: Control<any>;
  isPaidInitial: boolean;
  instructors: Option[];
  stringOptions: { value: string | number; label: string }[];
  serviceOptions: Option[];
  timeOptions: Option[];
};

const LessonDetailsSection: React.FC<LessonDetailsSectionProps> = ({
  control,
  isPaidInitial,
  instructors,
  stringOptions,
  serviceOptions,
  timeOptions
}) => {
  return (
    <div className='overflow-x-auto'>
      <table
        className='w-full border border-gray-300 rounded-xl text-center mx-auto mb-4 md:text-sm text-xs'
        style={{ minWidth: '1100px' }}
      >
        <colgroup>
          <col style={{ width: '15%' }} /> {/*  Instructor */}
          <col style={{ width: '4%' }} /> {/* String*/}
          <col style={{ width: '18%' }} /> {/* Client*/}
          <col style={{ width: '6%' }} /> {/* REQ*/}
          <col style={{ width: '8%' }} /> {/* Service*/}
          <col style={{ width: '4%' }} /> {/* AM*/}
          <col style={{ width: '4%' }} /> {/* PM*/}
          <col style={{ width: '4%' }} /> {/* FD*/}
          <col style={{ width: '7%' }} /> {/* Level*/}
          <col style={{ width: '7%' }} /> {/* Start*/}
          <col style={{ width: '7%' }} /> {/* End*/}
        </colgroup>

        <thead className='bg-blue-100 text-gray-800'>
          <tr>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              Instructor <span className='text-red-500'>*</span>
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              String
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              Client <span className='text-red-500'>*</span>
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              REQ <span className='text-red-500'>*</span>
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              Service <span className='text-red-500'>*</span>
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              AM
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              PM
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              FD
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              Level
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              Start
            </th>
            <th className='md:py-2 md:px-2.5 py-1.5 px-1.5 border whitespace-nowrap'>
              End
            </th>
          </tr>
        </thead>

        <tbody className='bg-white divide-y divide-gray-200'>
          <tr className='bg-white hover:bg-gray-50'>
            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle overflow-hidden'>
              <FormSelect
                name='instructor_id'
                control={control}
                options={instructors}
                placeholder='Select instructor'
                disabled={isPaidInitial}
                searchable
                searchPlaceholder='Search instructor...'
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='string'
                control={control}
                options={stringOptions}
                placeholder='Select String'
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormInput
                name='client_name'
                label=''
                control={control}
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='lesson_req'
                control={control}
                options={REQ_OPTIONS}
                placeholder='Select REQ'
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='lesson_code_id'
                control={control}
                options={serviceOptions}
                placeholder='Select service'
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='am'
                control={control}
                options={CreateArrayOptions(100, 'Select AM')}
                placeholder='Select AM'
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='pm'
                control={control}
                options={CreateArrayOptions(100, 'Select PM')}
                placeholder='Select PM'
                disabled={isPaidInitial}
              />
            </td>

            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='fd'
                control={control}
                options={CreateArrayOptions(100, 'Select FD')}
                placeholder='Select FD'
                disabled={isPaidInitial}
              />
            </td>
            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='ski_level'
                control={control}
                label=''
                options={LEVEL_OPTIONS}
                placeholder='Select level'
                disabled={isPaidInitial}
              />
            </td>
            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormSelect
                name='start'
                control={control}
                options={timeOptions}
                placeholder='Select start time'
                disabled={isPaidInitial}
              />
            </td>
            <td className='border md:px-2 md:py-1.5 px-1 py-1 align-middle'>
              <FormInput name='end' control={control} label='' disabled />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default LessonDetailsSection;
